/**
 * Formato de precios de planes, y el IVA chileno.
 *
 * ## Por qué existe
 *
 * El precio de un plan se muestra en dos pantallas —la landing pública y
 * `/subscription`— y estaban formateándolo distinto: la landing con
 * `Intl.NumberFormat` y la de suscripción con un `$` tecleado a mano. Al agregar el
 * IVA habría quedado un tercer formato. Es el mismo motivo por el que existe
 * `planFeatures.js`: la lista de features también se derivaba dos veces.
 *
 * ## El IVA va dentro del precio
 *
 * A un cliente chileno hay que cobrarle IVA 19%, y LemonSqueezy no lo cobra: es
 * Merchant of Record y no está registrado en Chile. Así que el 19% viaja **dentro**
 * del precio, con una variante de LS más cara para Chile.
 *
 * El precio de lista que devuelve el backend (`price_monthly` / `price_yearly`) es
 * **neto** y se queda así: lo leen el presupuesto de IA (20% del precio de lista) y el
 * MRR. El bruto se calcula acá, al mostrar.
 *
 * ⚠️ Este cálculo está duplicado a propósito con `services/impuestos.py`, que es el
 * que manda. Acá es sólo para mostrar: lo que se cobra de verdad lo decide la variante
 * de LemonSqueezy que elige el backend. Si los dos discreparan, el que tiene razón es
 * el backend y esto es un bug de presentación, no de cobro.
 */

/** Tasa de IVA por país de facturación. Sólo Chile, que es donde somos contribuyentes. */
const TASAS = { CL: 0.19 };

/** La tasa que aplica a `country`, o 0 si no corresponde impuesto. */
export function taxRate(country) {
  if (!country) return 0;
  return TASAS[String(country).toUpperCase()] || 0;
}

/** `true` si a este país hay que mostrarle el precio con impuesto incluido. */
export function hasTax(country) {
  return taxRate(country) > 0;
}

/**
 * El precio que paga un cliente de `country`: neto + impuesto.
 *
 * Devuelve un número, no un string, para que quien llame decida el formato.
 */
export function grossPrice(net, country) {
  const n = parseFloat(net);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * (1 + taxRate(country)) * 100) / 100;
}

/**
 * Formatea un monto con el símbolo de la moneda del plan.
 *
 * `narrowSymbol` lo deja compacto ("$" en vez de "US$"); el código de moneda que se
 * muestra al lado desambigua las que comparten símbolo (USD vs CLP).
 *
 * `decimals` existe porque el precio neto es redondo (US$119) y el bruto casi nunca
 * lo es (US$141,61): mostrar el bruto sin decimales lo redondearía a 142 y el número
 * no cuadraría con el cargo de la tarjeta.
 */
export function formatAmount(value, currency, language, decimals = 0) {
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency: currency || "USD",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: "always",
  }).format(value);
}

/**
 * El precio a mostrar para un plan, ya con impuesto si corresponde.
 *
 * Devuelve `{ amount, formatted, net, netFormatted, tax, taxRate, isFree }` para que
 * la vista pueda mostrar el total y, si quiere, el desglose. `country` puede venir
 * `null` (la landing no sabe quién mira) y entonces se muestra el neto.
 */
export function planPrice(plan, yearly, language, country) {
  const net = yearly ? plan.price_yearly : plan.price_monthly;
  const currency = plan.currency || "USD";
  const rate = taxRate(country);
  const gross = grossPrice(net, country);
  // El bruto lleva decimales sólo cuando no es redondo: 141,61 los necesita, 119 no.
  const grossDecimals = Number.isInteger(gross) ? 0 : 2;

  return {
    isFree: !net || parseFloat(net) === 0,
    net: parseFloat(net) || 0,
    netFormatted: formatAmount(parseFloat(net) || 0, currency, language, 0),
    amount: gross,
    formatted: formatAmount(gross, currency, language, grossDecimals),
    tax: Math.round((gross - (parseFloat(net) || 0)) * 100) / 100,
    taxRate: rate,
    currency,
  };
}
